import React, { useState, useEffect } from "react";
import { fetchBranchesData, updateCustomProductLimit, updateCustomBranchLimit, toggleEnableFreeCash, updateBrainDailyRequestLimit, updateZuntlyAiDailyRequestLimit } from "../services/ClientService";
import SnackBar from "./SnackBar";
import SubscriptionManager from "./Subscription/SubscriptionManager";

const AdminPermissions = () => {
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [snackBarMessage, setSnackBarMessage] = useState("");
    const [isSnackBarVisible, setIsSnackBarVisible] = useState(false);
    const [activeTab, setActiveTab] = useState('online');

    const defaultLimits = {
        Bronze: 3,
        Silver: 60,
        Gold: 80,
        Platinum: Infinity
    };

    const defaultBranchLimits = {
        Bronze: 4,
        Silver: 4,
        Gold: 4,
        Platinum: 4
    };

    useEffect(() => {
        fetchBranches();
    }, []);

    const fetchBranches = async () => {
        setLoading(true);
        try {
            const response = await fetchBranchesData();
            if (response.success) {
                setBranches(response.data);
            } else {
                setSnackBarMessage("Failed to fetch branches");
                setIsSnackBarVisible(true);
            }
        } catch (error) {
            setSnackBarMessage("Error fetching branches");
            setIsSnackBarVisible(true);
        } finally {
            setLoading(false);
        }
    };

    const handleLimitChange = (branchId, value, type) => {
        setBranches(prev => prev.map(branch =>
            branch._id === branchId ? { ...branch, [type]: value ? parseInt(value) : null } : branch
        ));
    };

    const handleToggleFreeCash = async (branchId, nextValue) => {
        setSaving((prev) => ({ ...prev, [branchId]: true }));
        try {
            const response = await toggleEnableFreeCash({ _id: branchId, enableFreeCash: nextValue });
            if (response.success) {
                setBranches((prev) => prev.map((branch) =>
                    branch._id === branchId ? { ...branch, enableFreeCash: nextValue } : branch
                ));
                setSnackBarMessage(response.message || `Free Cash ${nextValue ? "enabled" : "disabled"}`);
                setIsSnackBarVisible(true);
            } else {
                setSnackBarMessage("Failed to update Free Cash permission");
                setIsSnackBarVisible(true);
            }
        } catch (error) {
            setSnackBarMessage("Error updating Free Cash permission");
            setIsSnackBarVisible(true);
        } finally {
            setSaving((prev) => ({ ...prev, [branchId]: false }));
        }
    };

    const handleSave = async (branchId) => {
        const branch = branches.find(b => b._id === branchId);
        if (!branch) return;

        setSaving(prev => ({ ...prev, [branchId]: true }));
        try {
            let response;
            if (activeTab === 'branch') {
                response = await updateCustomBranchLimit({
                    _id: branchId,
                    customBranchLimit: branch.customBranchLimit
                });
            } else if (activeTab === 'brainLimit') {
                response = await updateBrainDailyRequestLimit({
                    _id: branchId,
                    brainDailyRequestLimit: branch.brainDailyRequestLimit
                });
            } else if (activeTab === 'zuntlyAiLimit') {
                response = await updateZuntlyAiDailyRequestLimit({
                    _id: branchId,
                    zuntlyAiDailyRequestLimit: branch.zuntlyAiDailyRequestLimit
                });
            } else {
                response = await updateCustomProductLimit({
                    _id: branchId,
                    customOnlineProductLimit: branch.customOnlineProductLimit,
                    customOfflineProductLimit: branch.customOfflineProductLimit
                });
            }
            if (response.success) {
                const successMessages = {
                    branch: "Branch limit updated successfully",
                    brainLimit: "Zuntly Brain daily request limit updated successfully",
                    zuntlyAiLimit: "Zuntly AI monthly request limit updated successfully",
                };
                setSnackBarMessage(successMessages[activeTab] || "Product limits updated successfully");
                setIsSnackBarVisible(true);
            } else {
                const failMessages = {
                    branch: "Failed to update branch limit",
                    brainLimit: "Failed to update Zuntly Brain daily request limit",
                    zuntlyAiLimit: "Failed to update Zuntly AI monthly request limit",
                };
                setSnackBarMessage(failMessages[activeTab] || "Failed to update product limits");
                setIsSnackBarVisible(true);
            }
        } catch (error) {
            const errorMessages = {
                branch: "Error updating branch limit",
                brainLimit: "Error updating Zuntly Brain daily request limit",
                zuntlyAiLimit: "Error updating Zuntly AI monthly request limit",
            };
            setSnackBarMessage(errorMessages[activeTab] || "Error updating product limits");
            setIsSnackBarVisible(true);
        } finally {
            setSaving(prev => ({ ...prev, [branchId]: false }));
        }
    };

    const getCurrentLimit = (branch, type) => {
        const customLimit = branch[type];
        let subscription;
        if (activeTab === 'online') {
            subscription = branch.selectedSubscription;
        } else if (activeTab === 'offline') {
            subscription = branch.selectedOfflineSubscription;
        } else if (activeTab === 'branch') {
            subscription = branch.selectedOfflineSubscription || branch.selectedSubscription;
        }
        if (activeTab === 'branch') {
            return customLimit || (subscription ? defaultBranchLimits[subscription] || 'Unlimited' : 'Unlimited');
        } else {
            return customLimit || (subscription ? defaultLimits[subscription] || 'Unlimited' : 'Unlimited');
        }
    };

    const getDefaultLimit = (subscription) => {
        if (activeTab === 'branch') {
            return subscription ? defaultBranchLimits[subscription] || 'Unlimited' : 'Unlimited';
        } else {
            return subscription ? defaultLimits[subscription] || 'Unlimited' : 'Unlimited';
        }
    };

    const filteredBranches = branches.filter(branch => branch.masterAccount === true);

    return (
        <div className="h-screen overflow-hidden max-h-fit bg-white">
            <div className="bg-white p-6 sticky top-0 z-10 space-y-4">
                <h1 className="text-3xl font-bold">Admin Permissions</h1>
                <p className="text-gray-600">Manage product limits and feature permissions for client subscriptions</p>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setActiveTab('online')}
                        className={`px-4 py-2 rounded-md ${activeTab === 'online' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Online Stores
                    </button>
                    <button
                        onClick={() => setActiveTab('offline')}
                        className={`px-4 py-2 rounded-md ${activeTab === 'offline' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Offline Stores
                    </button>
                    <button
                        onClick={() => setActiveTab('branch')}
                        className={`px-4 py-2 rounded-md ${activeTab === 'branch' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Branch Limits
                    </button>
                    <button
                        onClick={() => setActiveTab('freeCash')}
                        className={`px-4 py-2 rounded-md ${activeTab === 'freeCash' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Free Cash
                    </button>
                    <button
                        onClick={() => setActiveTab('brainLimit')}
                        className={`px-4 py-2 rounded-md ${activeTab === 'brainLimit' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Brain Request Limit
                    </button>
                    <button
                        onClick={() => setActiveTab('zuntlyAiLimit')}
                        className={`px-4 py-2 rounded-md ${activeTab === 'zuntlyAiLimit' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Zuntly AI Request Limit
                    </button>
                    <button
                        onClick={() => setActiveTab('subscription')}
                        className={`px-4 py-2 rounded-md ${activeTab === 'subscription' ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Subscriptions & Billing
                    </button>
                </div>
            </div>

            <div className="p-6">
                {activeTab === 'subscription' ? (
                    <div className="max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
                        <SubscriptionManager />
                    </div>
                ) : loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredBranches.map(branch => (
                            <div key={branch._id} className={`bg-gray-50 p-6 rounded-lg shadow-sm border ${((activeTab === 'online' && !branch.enableOnlineStore) || (activeTab === 'offline' && !branch.enableOfflineStore)) ? 'grayscale opacity-50 relative' : ''}`}>
                                {activeTab === 'freeCash' ? (
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-lg font-semibold">{branch.branchName}</h3>
                                            <p className="text-sm text-gray-500 mt-1">
                                                Allow this master to use Free Cash in the client portal and customer app.
                                            </p>
                                        </div>
                                        <label className="inline-flex items-center gap-3 cursor-pointer">
                                            <span className="text-sm font-medium text-gray-700">
                                                {branch.enableFreeCash ? "Enabled" : "Disabled"}
                                            </span>
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={!!branch.enableFreeCash}
                                                disabled={!!saving[branch._id]}
                                                onClick={() => handleToggleFreeCash(branch._id, !branch.enableFreeCash)}
                                                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                                                    branch.enableFreeCash ? "bg-teal-600" : "bg-gray-300"
                                                } disabled:opacity-50`}
                                            >
                                                <span
                                                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                                        branch.enableFreeCash ? "translate-x-6" : "translate-x-1"
                                                    }`}
                                                />
                                            </button>
                                        </label>
                                    </div>
                                ) : activeTab === 'brainLimit' ? (
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-lg font-semibold">{branch.branchName}</h3>
                                            <p className="text-sm text-gray-500 mt-1">
                                                Maximum Zuntly Brain AI commands this master can run per day (inventory + expiry).
                                                Resets every day at 12:00 AM IST. Leave empty for unlimited.
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                Current: {branch.brainDailyRequestLimit ? `${branch.brainDailyRequestLimit} requests/day` : 'Unlimited'}
                                            </p>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <div className="text-right">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Requests per day
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={branch.brainDailyRequestLimit || ''}
                                                    onChange={(e) => handleLimitChange(branch._id, e.target.value, 'brainDailyRequestLimit')}
                                                    placeholder="Unlimited"
                                                    className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                            <button
                                                onClick={() => handleSave(branch._id)}
                                                disabled={!!saving[branch._id]}
                                                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {saving[branch._id] ? 'Saving...' : 'Save'}
                                            </button>
                                        </div>
                                    </div>
                                ) : activeTab === 'zuntlyAiLimit' ? (
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-lg font-semibold">{branch.branchName}</h3>
                                            <p className="text-sm text-gray-500 mt-1">
                                                Maximum Zuntly AI revenue analyses this master can run per month.
                                                Resets on the 1st of every month at 12:00 AM IST. Leave empty for unlimited.
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                Current: {branch.zuntlyAiDailyRequestLimit ? `${branch.zuntlyAiDailyRequestLimit} requests/month` : 'Unlimited'}
                                            </p>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <div className="text-right">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Requests per month
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={branch.zuntlyAiDailyRequestLimit || ''}
                                                    onChange={(e) => handleLimitChange(branch._id, e.target.value, 'zuntlyAiDailyRequestLimit')}
                                                    placeholder="Unlimited"
                                                    className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                            <button
                                                onClick={() => handleSave(branch._id)}
                                                disabled={!!saving[branch._id]}
                                                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {saving[branch._id] ? 'Saving...' : 'Save'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex justify-between items-center mb-4">
                                            <div>
                                                <h3 className="text-lg font-semibold">{branch.branchName}</h3>
                                                <p className="text-gray-600">Subscription: {activeTab === 'online' ? branch.selectedSubscription : activeTab === 'offline' ? branch.selectedOfflineSubscription : branch.selectedOfflineSubscription || branch.selectedSubscription || 'None'}</p>
                                                <p className="text-sm text-gray-500">
                                                    Default Limit: {getDefaultLimit(activeTab === 'online' ? branch.selectedSubscription : activeTab === 'offline' ? branch.selectedOfflineSubscription : branch.selectedOfflineSubscription || branch.selectedSubscription)}
                                                </p>
                                            </div>
                                            <div className="flex items-center space-x-4">
                                                <div className="text-right">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Custom {activeTab === 'online' ? 'Online' : activeTab === 'offline' ? 'Offline' : ''} {activeTab === 'branch' ? 'Branch' : 'Product'} Limit
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={branch[activeTab === 'online' ? 'customOnlineProductLimit' : activeTab === 'offline' ? 'customOfflineProductLimit' : 'customBranchLimit'] || ''}
                                                        onChange={(e) => handleLimitChange(branch._id, e.target.value, activeTab === 'online' ? 'customOnlineProductLimit' : activeTab === 'offline' ? 'customOfflineProductLimit' : 'customBranchLimit')}
                                                        placeholder="Leave empty for default"
                                                        disabled={(activeTab === 'online' && !branch.enableOnlineStore) || (activeTab === 'offline' && !branch.enableOfflineStore)}
                                                        className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                    />
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Current: {getCurrentLimit(branch, activeTab === 'online' ? 'customOnlineProductLimit' : activeTab === 'offline' ? 'customOfflineProductLimit' : 'customBranchLimit')}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => handleSave(branch._id)}
                                                    disabled={saving[branch._id] || (activeTab !== 'branch' && ((activeTab === 'online' && !branch.enableOnlineStore) || (activeTab === 'offline' && !branch.enableOfflineStore)))}
                                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {saving[branch._id] ? 'Saving...' : 'Save'}
                                                </button>
                                            </div>
                                        </div>
                                        {((activeTab === 'online' && !branch.enableOnlineStore) || (activeTab === 'offline' && !branch.enableOfflineStore)) && (
                                            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                                                <p className="text-center text-gray-700 font-medium px-4">{activeTab === 'online' ? 'This online store is disabled. Please go to branches and make it active to make changes.' : 'This offline store is disabled. Please go to branches and make it active to make changes.'}</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <SnackBar
                message={snackBarMessage}
                isVisible={isSnackBarVisible}
                onClose={() => setIsSnackBarVisible(false)}
            />
        </div>
    );
};

export default AdminPermissions;
